import axios from "axios";
import {
  BusinessTrip,
  BusinessTripExpense,
  ExpenseFetchResponse,
} from "types/BusinessTrip";
import { API_URL } from "../constants";

export interface BusinessTripsPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

export type BusinessTripsResponse =
  | BusinessTrip[]
  | {
      businessTripsCollection: BusinessTrip[];
      pagination: BusinessTripsPagination;
    };

export async function fetchBusinessTrips(
  companyId: string,
  filtersValue: any,
  options: { page?: number; pageSize?: number } = {}
): Promise<BusinessTripsResponse> {
  // Pagination is opt-in. Without `page` the backend returns the full bare
  // array (kept for any non-paginated caller of this endpoint).
  const body: any = { ...filtersValue };
  if (options && options.page != null) {
    body.page = options.page;
    body.pageSize = options.pageSize;
  }
  const res = await axios.post(
    `${API_URL}/${companyId}/businesstrip/fetch`,
    body
  );
  return res.data as BusinessTripsResponse;
}

export type UserBusinessTripsResponse =
  | BusinessTrip[]
  | {
      entries: BusinessTrip[];
      pagination: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      };
    };

export async function fetchUserBusinessTrips(
  companyId: string,
  filtersValue: {
    textSearch?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  } = {}
): Promise<UserBusinessTripsResponse> {
  const res = await axios.post(
    `${API_URL}/${companyId}/user/businesstrip/fetch`,
    filtersValue
  );
  return res.data as UserBusinessTripsResponse;
}

export async function fetchUserBusinessTripsForLeave(
  companyId: string,
  params: {
    startDate?: string;
    endDate?: string;
  }
): Promise<BusinessTrip[]> {
  const res = await axios.post(
    `${API_URL}/${companyId}/user/businesstrip/fetch`,
    {
      startDate: params.startDate,
      endDate: params.endDate,
    }
  );
  return res.data as BusinessTrip[];
}

export async function createBusinessTrip(
  companyId: string,
  data: Partial<BusinessTrip>
): Promise<BusinessTrip> {
  const res = await axios.post(
    `${API_URL}/${companyId}/user/businesstrip/post`,
    data
  );
  return res.data as BusinessTrip;
}

export async function fetchBusinessTripById(
  tripId: string,
  companyId: string
): Promise<BusinessTrip> {
  const res = await axios.get(
    `${API_URL}/${companyId}/businesstrip/${tripId}/fetch-by-id`
  );
  return res.data;
}

export async function updateBusinessTrip(
  companyId: string,
  id: string,
  data: Partial<BusinessTrip>
): Promise<{ documentInvalidated: boolean }> {
  const response = await axios.patch(
    `${API_URL}/${companyId}/businesstrip/${id}/patch`,
    data
  );
  return response.data ?? { documentInvalidated: false };
}

/** Create or update business trip report; saves report fields on trip and generates/updates report PDF. */
export async function createOrUpdateBusinessTripReport(
  companyId: string,
  tripId: string,
  data: Partial<BusinessTrip>
): Promise<{ report: BusinessTrip; file: any }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/businesstrip/${tripId}/report`,
    data
  );
  return res.data;
}

/**
 * Super-admin (Master) override: void a report document so the report can
 * be redone. A FULLY signed report file is marked annulled and kept as
 * evidence (a stamped "ANULIUOTA" copy replaces it in the UI, signers are
 * notified by email); a not-yet-signed one is still deleted outright.
 * Either way the link to the trip is removed, report data stays on the
 * trip, and status resets to pending_report.
 */
export async function annulBusinessTripReport(
  companyId: string,
  tripId: string,
  reason?: string
): Promise<{ status: string; annulledFileIds: string[] }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/businesstrip/${tripId}/report/annul`,
    { reason: reason ?? null }
  );
  return res.data;
}

// --- Expense endpoints ---

export async function fetchTripExpenses(
  companyId: string,
  tripId: string
): Promise<ExpenseFetchResponse> {
  const res = await axios.get(
    `${API_URL}/${companyId}/businesstrip/${tripId}/expenses`
  );
  return res.data;
}

export async function createTripExpense(
  companyId: string,
  tripId: string,
  data: Partial<BusinessTripExpense>
): Promise<BusinessTripExpense> {
  const res = await axios.post(
    `${API_URL}/${companyId}/businesstrip/${tripId}/expenses`,
    data
  );
  return res.data;
}

export async function updateTripExpense(
  companyId: string,
  tripId: string,
  expenseId: string,
  data: Partial<BusinessTripExpense>
): Promise<BusinessTripExpense> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/businesstrip/${tripId}/expenses/${expenseId}`,
    data
  );
  return res.data;
}

export async function deleteTripExpense(
  companyId: string,
  tripId: string,
  expenseId: string
): Promise<void> {
  await axios.delete(
    `${API_URL}/${companyId}/businesstrip/${tripId}/expenses/${expenseId}`
  );
}

export async function uploadExpenseReceipt(
  companyId: string,
  tripId: string,
  file: File
): Promise<{ fileId: string; url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(
    `${API_URL}/${companyId}/businesstrip/${tripId}/expenses/upload-receipt`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data;
}
